import React, { useMemo, useState, useRef } from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { Category } from "../../lib/sanity/types";

interface BlogCategorySliderProps {
  categories: Category[];
  onCategoryChange: (categorySlug: string) => void;
}

const BlogCategorySlider: React.FC<BlogCategorySliderProps> = ({
  categories,
  onCategoryChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeCategory, setActiveCategory] = useState("all");
  const sliderRef = useRef<HTMLDivElement | null>(null);

  const allCategories = useMemo(
    () => [{ _id: "all", title: "All", slug: "all" }, ...categories],
    [categories]
  );

  const handleCategoryClick = (category: any) => {
    const slug = category.slug || "all";
    setActiveCategory(slug);
    onCategoryChange(slug);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const slider = sliderRef.current;
    if (!slider) return;

    let isDown = false;
    let startX = e.pageX - slider.offsetLeft;
    let scrollLeft = slider.scrollLeft;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2;
      slider.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      isDown = false;
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    isDown = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  // desktop
  if (!isMobile) {
    return (
      <Box
        ref={sliderRef}
        onMouseDown={handleMouseDown}
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          justifyContent: "center",
          pb: 2,
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {allCategories.map((cat) => {
          const isActive = activeCategory === cat.slug;
          return (
            <Box
              key={cat._id}
              onClick={() => handleCategoryClick(cat)}
              sx={{
                minWidth: 260,
                height: 140,
                borderRadius: 2,
                p: 2.5,
                position: "relative",
                cursor: "pointer",
                bgcolor: isActive ? "#6366f1" : "rgba(148,163,184,0.12)",
                color: isActive ? "#fff" : "#1e293b",
                transition: "transform .2s ease, opacity .2s ease",
                "&:hover": {
                  transform: "scale(1.03)",
                  opacity: 0.95,
                },
              }}
            >
              <Box sx={{ fontSize: 20, fontWeight: 700 }}>{cat.title}</Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  // mobile
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 1.5,
      }}
    >
      {allCategories.map((cat) => {
        const isActive = activeCategory === cat.slug;
        return (
          <Button
            key={cat._id}
            onClick={() => handleCategoryClick(cat)}
            sx={{
              justifyContent: "flex-start",
              gap: 1.5,
              textTransform: "none",
              borderRadius: 2,
              bgcolor: isActive ? "#6366f1" : "rgba(15,23,42,0.35)",
              color: "#fff",
              "&:hover": {
                bgcolor: isActive ? "#6366f1" : "rgba(15,23,42,0.55)",
              },
            }}
          >
            {cat.title}
          </Button>
        );
      })}
    </Box>
  );
};

export default BlogCategorySlider;
